<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" aria-label="Close" @click="doCancel">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">{{ title }}</h5>
    </div>
    <div class="offcanvas-body">
      <div class="alert alert-warning" role="alert" v-if="!clinic.email">
        <p>{{ $t('exam.exportEmailWarning') }}</p>
      </div>

      <div class="download-dates" v-show="!loadingDownload && !requestAccepted">
        <!-- Start Date Picker -->
        <div class="form-floating">
          <date-picker
            v-model="startDate"
            :locale="locale"
            value-type="date"
            :selectText="$t('exam.select')"
            :cancelText="$t('cancel')"
            :format="$t('dateFormatFns')"
            :inputFormat="$t('dateFormatFns')"
            :enableTimePicker="false"
            :disabled-date="disabledStartDate"
            data-test="datePickerStart"/>
          <label>{{ $t('general.startDate') }}</label>
        </div>

        <!-- End Date Picker -->
        <div class="form-floating">
          <date-picker
            v-model="endDate"
            :locale="locale"
            value-type="date"
            :selectText="$t('exam.select')"
            :cancelText="$t('cancel')"
            :format="$t('dateFormatFns')"
            :inputFormat="$t('dateFormatFns')"
            :enableTimePicker="false"
            :disabled-date="disabledEndDate"
            data-test="datePickerEnd"/>
          <label>{{ $t('general.endDate') }}</label>
        </div>

        <!-- CC e-mails -->
        <div class="cc-emails">
          <BaseEmailTagInput v-model="ccEmails" data-test="inputCcEmails"/>
          <small class="form-hint">{{ $t('exam.bulkExportCcEmailsHint') }}</small>
        </div>
      </div>

      <div class="text-center" v-show="loadingDownload && !requestAccepted">
        <h3>{{ $t('exam.preparingFileForDownload') }}</h3>
        <img class="mb-3" src="@/assets/images/loading_points.gif" alt=""/>
      </div>

      <div class="text-center" v-show="!loadingDownload && requestAccepted">
        <h3>{{ $t('exam.bulkExportRequested') }}</h3>
        <p>{{ $t('exam.bulkExportEmailSoon') }}</p>
      </div>
    </div>
    <div class="offcanvas-footer" v-if="!loadingDownload && !requestAccepted">
      <button type="button" class="btn" :class="okButtonClass" data-test="btnConfirm" @click="doConfirm">{{ okButton }}</button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  .offcanvas-title,
  h3 {
    color:                        var(--Brand-Primary, #1B294A);
    font-size:                    38px;
    font-weight:                  300;
    line-height:                  normal;
    margin:                       26px 0;
  }

  h3 {
    font-size:                    var(--font-size-18);
  }

  .offcanvas-body {
    p, li {
      color:                      var(--Brand-Primary, #1B294A);
      font-size:                  var(--font-size-14);
      font-weight:                300;
      line-height:                140%;
      margin-bottom:              26px;
    }

    .download-dates {
      display:                    flex;
      flex-direction:             column;
      gap:                        19px;

      .form-floating label {
        padding-top:              0.4rem;
      }

      .cc-emails {
        .form-hint {
          display:                block;
          margin-top:             6px;
          color:                  var(--Shades-50);
          font-size:              var(--font-size-12);
          font-weight:            300;
          line-height:            140%;
        }
      }
    }

    .alert-warning {
      p {
        color:                    #1B294A;
        margin:                   0;
      }
    }
  }

  .btn {
    font-size:                    var(--font-size-14);
    font-weight:                  700;
    letter-spacing:               4.2px;
    text-align:                   center;
    text-transform:               uppercase;
    width:                        100%;
    padding:                      20px;

    &.btn-outline-secondary {
      padding:                    20px 18px;
    }
  }

  .offcanvas-footer {
    display:                      flex;
    flex-direction:               column;
    gap:                          26px;
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'

export default {
  name: 'ExamClaimBulkExportOffcanvas',
  props: {
    offcanvas_id: String
  },
  data: () => ({
    alert: {
      message: null,
      class: null
    },
    title: null,
    text: null,
    okButton: null,
    okButtonClass: null,
    offcanvas_download: null,
    clinicUUID: null,
    clinic: {},
    loadingDownload: false,
    requestAccepted: false,
    startDate: new Date(),
    endDate: new Date(),
    ccEmails: [],
    session: {
      collaborator: {},
      currentClinic: {},
      features: []
    },
  }),
  computed: {
    locale() {
      return this.$utils.datePickerLocale()
    }
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
  },
  methods: {
    show(options = {}) {
      this.clearForm()

      if (options.title)
        this.title = options.title

      if (options.text)
        this.text = options.text

      if (options.clinicUUID)
        this.clinicUUID = options.clinicUUID

      if (options.okButton) {
        this.okButton = options.okButton
        this.okButtonClass = options.okButtonClass
      }

      if (options.clinicID)
        this.fetchData(options.clinicID)

      this.offcanvas_download = new bootstrap.Offcanvas(document.getElementById(this.offcanvas_id))
      this.offcanvas_download.show();

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    doConfirm() {
      if (!this.clinic.email) {
        this.$message.popup(this, this.$t('exam.bulkExportNoEmail'), 'operationFail', 'error')
        return
      }

      if (this.startDate && this.endDate && new Date(this.startDate) > new Date(this.endDate)) {
        this.$message.popup(this, this.$t('exam.startDateAfterEndDate'), 'operationFail', 'error')
        return
      }

      this.generateClaimBulk(this.ccEmails)
      this.resolvePromise(true)
    },

    doCancel() {
      this.loadingDownload = false
      this.requestAccepted = false
      this.ccEmails = []
      this.offcanvas_download = null;
      this.resolvePromise(false)
    },

    disabledStartDate(date) {
      return date > new Date()
    },

    disabledEndDate(date) {
      if (date > new Date()) return true
      if (this.startDate && date < new Date(this.startDate)) return true
      return false
    },

    async fetchData(clinicID) {
      let params = {
        id: clinicID
      }

      try {
        let response = await this.$clinicService.getClinic(params);
        this.clinic = response.clinic
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
    },

    async generateClaimBulk(ccEmails = []) {
      this.alert.message = null;

      let params = {
        clinicId: this.clinicUUID,
        startDate: this.$utils.formatDate(this, this.startDate),
        endDate: this.$utils.formatDate(this, this.endDate),
        ccEmails,
        userOffset: new Date().getTimezoneOffset(),
      };

      this.loadingDownload = true;
      NProgress.start()
      try {
        let response = await this.$claimConfigService.exportBatchClaimsFromExams(params);
        this.requestAccepted = response?.status === 202;
        if (!this.requestAccepted) {
          this.$message.popup(this, 'request_error', 'operationFail', 'error')
        }
      } catch(err) {
        const errorMessage = err.response?.data?.errorMessage
        this.$message.popup(this, errorMessage || 'request_error', 'operationFail', 'error')
      } finally {
        this.loadingDownload = false;
        NProgress.done(true)
      }
    },

    clearForm() {
      this.clinic = {}
      this.clinicUUID = null
      this.loadingDownload = false
      this.requestAccepted = false
      this.startDate = new Date()
      this.endDate = new Date()
      this.ccEmails = []
    }
  }
}
</script>
